/**
 * alignTranscript — tách transcript thành chunks, mỗi chunk khớp 1 câu lyric.
 *
 * Thuật toán: greedy forward alignment.
 *   - Với từng câu lyric theo thứ tự, tìm "best window" trong phần transcript
 *     còn lại (chưa gán cho câu trước) mà có độ khớp cao nhất.
 *   - Window size = số từ lyric của câu đó ± tolerance (50%).
 *   - Câu cuối lấy tất cả phần transcript còn lại.
 *
 * @param {string}   transcript  Toàn bộ chuỗi speech recognition.
 * @param {string[]} lyrics      Mảng các câu lyric.
 * @returns {{ chunk: string[], point: number, spokenLine: {word:string,matched:boolean}[] }[]}
 */
export function alignTranscript(transcript, lyrics) {
    const clean = s =>
        s.replace(/[!@#$%^&*(),.?"":{}|<>""''`';:\-]/g, '').toLowerCase().trim();

    const spokenWords = (transcript || '').trim().split(/\s+/).filter(Boolean);

    let offset = 0;

    return lyrics.map((line, idx) => {
        const lyricWords = (line || '').split(/\s+/).filter(Boolean);
        const lyricSet   = new Set(lyricWords.map(clean));
        const L          = lyricWords.length || 1;

        const remaining = spokenWords.slice(offset);

        let bestChunk = [];
        let bestScore = -1;

        if (idx === lyrics.length - 1) {
            // Last sentence: take everything left
            bestChunk = remaining;
        } else {
            // Try windows from (L * 0.5) to (L * 2) words
            const minW = Math.max(1, Math.floor(L * 0.5));
            const maxW = Math.min(remaining.length, Math.ceil(L * 2));

            for (let w = minW; w <= maxW; w++) {
                // Slide window: try starting at pos 0..tolerance
                const maxStart = Math.min(Math.floor(L * 0.5), remaining.length - w);
                for (let start = 0; start <= maxStart; start++) {
                    const candidate = remaining.slice(start, start + w);
                    const matches = candidate.filter(wd => lyricSet.has(clean(wd))).length;
                    // Penalise length deviation from L
                    const penalty = Math.abs(w - L) / L;
                    const score = matches / L - penalty * 0.3;
                    if (score > bestScore) {
                        bestScore = score;
                        bestChunk = candidate;
                        // If perfect match, no need to keep scanning
                        if (matches === L) { w = maxW + 1; break; }
                    }
                }
            }

            // Fallback: nothing matched at all → assign L words
            if (bestChunk.length === 0) {
                bestChunk = remaining.slice(0, L);
            }
        }

        // Advance offset by however many spoken words we consumed
        // (account for any start offset the best window started at)
        // Simple: advance by bestChunk length (greedy, no overlap)
        offset += bestChunk.length;

        // Score: how many lyric words appear in the chunk
        const chunkSet = new Set(bestChunk.map(clean));
        let matches = 0;
        const wordDetails = lyricWords.map(w => {
            const matched = chunkSet.has(clean(w));
            if (matched) matches++;
            return { word: w, matched };
        });
        const point = Math.ceil((matches / L) * 100);

        // Spoken line highlight: each spoken word vs lyric
        const spokenLine = bestChunk.map(w => ({
            word: w,
            matched: lyricSet.has(clean(w)),
        }));

        return { chunk: bestChunk, point, words: wordDetails, spokenLine };
    });
}
